import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, getApiBaseUrl } from '../lib/auth';
import { Eye, EyeOff, Loader2, Sparkles, ArrowRight, ShieldCheck, Settings } from 'lucide-react';

export default function LoginPage() {
  const { signIn, signInDemo, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();

  const [email, setEmail]       = useState('owner@zarinwear.com');
  const [password, setPassword] = useState('Zarin@2026');
  const [showPwd, setShowPwd]   = useState(false);
  const [apiUrl, setApiUrl]     = useState(getApiBaseUrl());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await signIn(email, password);
    if (useAuthStore.getState().session) {
      navigate('/dashboard', { replace: true });
    }
  };

  const handleDemoSignIn = async () => {
    clearError();
    await signInDemo();
    if (useAuthStore.getState().session) {
      navigate('/dashboard', { replace: true });
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logo}>
          <span style={styles.logoText}>ZARIN</span>
          <span style={styles.logoSub}>ADMIN ATELIER</span>
        </div>

        <h1 style={styles.title}>Welcome back</h1>
        <p style={styles.subtitle}>Sign in with Cloudflare Native Auth to manage atelier operations.</p>

        {/* Cloudflare Native Auth Notice & Quick Access */}
        <div style={styles.demoCard}>
          <div style={styles.demoCardHeader}>
            <ShieldCheck size={14} style={{ color: 'var(--admin-accent)' }} />
            <span style={styles.demoBadge}>Cloudflare Edge Native Auth</span>
          </div>
          <p style={styles.demoText}>
            D1 + WebCrypto authentication enabled. Use the seeded credentials below or instant preview.
          </p>
          <button
            type="button"
            onClick={handleDemoSignIn}
            style={styles.demoBtn}
            disabled={isLoading}
          >
            <Sparkles size={14} />
            <span>Instant Demo Access</span>
            <ArrowRight size={14} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form} noValidate>
          {error && (
            <div style={styles.errorBox} role="alert">
              <span>{error}</span>
            </div>
          )}

          <div style={styles.field}>
            <label htmlFor="email" style={styles.label}>Email address</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={styles.input}
              placeholder="admin@zarinwear.com"
              required
              disabled={isLoading}
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="password" style={styles.label}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPwd ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={{ ...styles.input, paddingRight: 44 }}
                placeholder="••••••••"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPwd(p => !p)}
                style={styles.eyeBtn}
                aria-label={showPwd ? 'Hide password' : 'Show password'}
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button type="submit" style={styles.submitBtn} disabled={isLoading || !email || !password}>
            {isLoading ? (
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              'Sign In with Credentials'
            )}
          </button>
        </form>

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--admin-border)', fontSize: 11, color: 'var(--admin-text-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <span>API: <code style={{ color: 'var(--admin-text)', fontSize: 10 }}>{apiUrl || 'local proxy'}</code></span>
          <button
            type="button"
            onClick={() => {
              const current = localStorage.getItem('zarin_api_url') || '';
              const next = prompt('Cloudflare Worker API URL (or leave blank for default):', current);
              if (next !== null) {
                if (next.trim()) {
                  localStorage.setItem('zarin_api_url', next.trim());
                } else {
                  localStorage.removeItem('zarin_api_url');
                }
                setApiUrl(getApiBaseUrl());
              }
            }}
            style={{ background: 'none', border: 'none', color: 'var(--admin-accent)', cursor: 'pointer', textDecoration: 'underline', fontSize: 11, padding: 0 }}
          >
            Change
          </button>
        </div>

        <p style={styles.footer}>
          Zarin Wear Atelier Administration • Confidential & Restricted
        </p>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: var(--admin-text-subtle); }
        input:focus { outline: none; border-color: var(--admin-accent) !important; box-shadow: 0 0 0 3px rgba(201,168,76,0.15); }
        button:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight:       '100dvh',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    background:      'var(--admin-bg)',
    padding:         24,
  },
  card: {
    width:           '100%',
    maxWidth:        420,
    background:      'var(--admin-surface)',
    border:          '1px solid var(--admin-border)',
    borderRadius:    'var(--radius-xl)',
    padding:         '36px 32px',
    boxShadow:       'var(--shadow-lg)',
  },
  logo: {
    display:         'flex',
    flexDirection:   'column',
    marginBottom:    24,
  },
  logoText: {
    fontFamily:      'var(--font-serif)',
    fontSize:        28,
    fontWeight:      300,
    letterSpacing:   '0.2em',
    color:           'var(--admin-text)',
    lineHeight:      1,
  },
  logoSub: {
    fontFamily:      'var(--font-sans)',
    fontSize:        9,
    fontWeight:      700,
    letterSpacing:   '0.35em',
    color:           'var(--admin-accent)',
    marginTop:       4,
  },
  title: {
    fontSize:        20,
    fontWeight:      600,
    color:           'var(--admin-text)',
    marginBottom:    6,
  },
  subtitle: {
    fontSize:        13,
    color:           'var(--admin-text-muted)',
    marginBottom:    24,
  },
  demoCard: {
    background:      'rgba(201, 168, 76, 0.08)',
    border:          '1px solid rgba(201, 168, 76, 0.25)',
    borderRadius:    'var(--radius-lg)',
    padding:         '16px',
    marginBottom:    24,
  },
  demoCardHeader: {
    display:         'flex',
    alignItems:      'center',
    gap:             6,
    marginBottom:    6,
  },
  demoBadge: {
    fontSize:        11,
    fontWeight:      600,
    letterSpacing:   '0.05em',
    textTransform:   'uppercase',
    color:           'var(--admin-accent)',
  },
  demoText: {
    fontSize:        12,
    color:           'var(--admin-text-muted)',
    lineHeight:      1.5,
    marginBottom:    12,
  },
  demoBtn: {
    width:           '100%',
    padding:         '10px 14px',
    background:      'var(--admin-accent)',
    color:           '#1A1A1A',
    border:          'none',
    borderRadius:    'var(--radius-md)',
    fontSize:        13,
    fontWeight:      600,
    cursor:          'pointer',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    gap:             8,
    transition:      'all var(--transition)',
  },
  form: {
    display:         'flex',
    flexDirection:   'column',
    gap:             16,
  },
  errorBox: {
    background:      'var(--admin-error-bg)',
    border:          '1px solid rgba(239,83,80,0.3)',
    borderRadius:    'var(--radius-md)',
    padding:         '10px 14px',
    fontSize:        13,
    color:           'var(--admin-error)',
  },
  field: {
    display:         'flex',
    flexDirection:   'column',
    gap:             6,
  },
  label: {
    fontSize:        12,
    fontWeight:      500,
    color:           'var(--admin-text-muted)',
    letterSpacing:   '0.03em',
  },
  input: {
    width:           '100%',
    padding:         '10px 12px',
    background:      'var(--admin-surface-2)',
    border:          '1px solid var(--admin-border)',
    borderRadius:    'var(--radius-md)',
    fontSize:        14,
    color:           'var(--admin-text)',
    transition:      'border-color 150ms, box-shadow 150ms',
    outline:         'none',
  },
  eyeBtn: {
    position:        'absolute',
    right:           12,
    top:             '50%',
    transform:       'translateY(-50%)',
    background:      'transparent',
    border:          'none',
    color:           'var(--admin-text-muted)',
    cursor:          'pointer',
    display:         'flex',
    padding:         4,
  },
  submitBtn: {
    width:           '100%',
    padding:         '11px',
    marginTop:       4,
    background:      'var(--admin-surface-3)',
    color:           'var(--admin-text)',
    border:          '1px solid var(--admin-border)',
    borderRadius:    'var(--radius-md)',
    fontSize:        13,
    fontWeight:      500,
    cursor:          'pointer',
    transition:      'background 150ms',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    gap:             8,
  },
  footer: {
    marginTop:       24,
    fontSize:        11,
    color:           'var(--admin-text-subtle)',
    textAlign:       'center',
    lineHeight:      1.6,
  },
};
